/**
 * Kaycoo Agentic Platform client
 * Connects KOIP to the shared Kaycoo AI platform at kaycoo-ai-platform-production.kaycoo.ai
 * Pattern mirrors D365-ai-assistant's agent-client.ts
 */

const PLATFORM_URL = import.meta.env.VITE_KAYCOO_PLATFORM_URL || 'https://kaycoo-ai-platform-production.kaycoo.ai';
const APPLICATION_ID = import.meta.env.VITE_KAYCOO_APP_ID || 'koip';

export interface PlatformAgent {
  assistant_id: string;
  agent_id?: string;
  name: string;
  description: string;
  model_id: string;
  provider: string;
  status: string;
  application_id?: string;
}

export interface AgentExecutionResponse {
  success?: boolean;
  response?: string;
  result?: string;
  output?: string;
  execution_time?: number;
  tokens?: number;
  model?: string;
  error?: string;
}

function getApiKey(): string | null {
  return localStorage.getItem('kaycoo_platform_api_key')
    || import.meta.env.VITE_KAYCOO_PLATFORM_API_KEY
    || null;
}

function buildHeaders(): HeadersInit {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  const apiKey = getApiKey();
  if (apiKey) {
    headers['X-API-Key'] = apiKey;
  }
  return headers;
}

export async function testPlatformConnection(): Promise<boolean> {
  try {
    const response = await fetch(`${PLATFORM_URL}/health`, {
      method: 'GET',
      headers: buildHeaders(),
    });
    return response.ok;
  } catch {
    return false;
  }
}

export async function fetchPlatformAgents(): Promise<PlatformAgent[]> {
  const url = `${PLATFORM_URL}/api/v1/agents?app_id=${encodeURIComponent(APPLICATION_ID)}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: buildHeaders(),
  });

  if (!response.ok) return [];

  const data = await response.json();
  const agents: PlatformAgent[] = Array.isArray(data)
    ? data
    : data.agents ?? data.data ?? [];

  agents.forEach((agent) => {
    if (!agent.agent_id && agent.assistant_id) {
      agent.agent_id = agent.assistant_id;
    }
  });

  return agents;
}

export async function executePlatformAgent(
  agentId: string,
  input: string,
): Promise<AgentExecutionResponse> {
  const response = await fetch(`${PLATFORM_URL}/api/v1/agents/${agentId}/execute`, {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify({ input }),
  });

  const raw = await response.json();
  if (!response.ok) {
    throw new Error(raw.detail || raw.error || 'Platform agent execution failed');
  }

  const data = (raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data))
    ? raw.data
    : raw;

  const text = data.response || data.result || data.output || '';
  if (text) {
    data.response = text;
    data.result = text;
  }

  return data;
}

export function getPlatformUrl(): string {
  return PLATFORM_URL;
}

export function getApplicationId(): string {
  return APPLICATION_ID;
}
